import { arrayIncludesUnknown } from '@hg-akademie-ban/vamos-viva-type-utils';
import { type MbscResource } from '@mobiscroll/react';
import { LocationOnOutlined } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { useConfiguredUrl } from '../../../../appRouter/url';
import { calendarColors, elevateColorPalette } from '../../../../theme/colorPalette';
import { HoverCard } from '../../HoverCard';
import {
  type CalendarExperteResource,
  type CalendarGruppenraumResource,
  type CalendarSeminarraumResource,
  type CalendarStandortResource,
  isExperteResource,
  isGruppenraumResource,
  isSeminarraumResource,
  isStandortResource,
} from '../helperClasses';
import { calendarCollapsibleRows } from '../helperClasses/calendarCollapsibleRows';
import { AbstractCalendarResource } from '../helperClasses/CalendarResource';
import { LocationResource } from '../helperClasses/CalendarResource/LocationResource';
import { ResourceTooltip } from './ResourceTooltip';

type ExperteResourceProps = {
  readonly resource: CalendarExperteResource;
};

const ExperteResource = (props: ExperteResourceProps) => {
  const { configuredUrl } = useConfiguredUrl();

  return (
    <a
      style={{ textDecoration: 'none', color: 'unset' }}
      href={configuredUrl.hive.experte.auslastung(props.resource.getRessourceSapId())}
      target="_blank"
      rel="noreferrer"
    >
      <Box display="flex" flexDirection="column">
        <Typography variant="labelLight">{props.resource.name}</Typography>
        <Typography variant="labelLight" color="textTertiary">
          {props.resource.subtext}
        </Typography>
      </Box>
    </a>
  );
};

type RaumOrStandortResourceProps = {
  readonly resource: CalendarSeminarraumResource | CalendarGruppenraumResource | CalendarStandortResource;
};

const RaumOrStandortResource = (props: RaumOrStandortResourceProps) => (
  <Box display="flex" flexDirection="column">
    <Typography variant="labelLight">{props.resource.name}</Typography>
    <Typography variant="labelLight" color="textTertiary" sx={{ mt: 0.5 }}>
      {props.resource.subtext}
    </Typography>
  </Box>
);

type ResourceGroupProps = {
  readonly resource: MbscResource;
};

const ResourceGroup = (props: ResourceGroupProps) => {
  const isTrainerGroup = arrayIncludesUnknown(
    [calendarCollapsibleRows.HAUPTTRAINER, calendarCollapsibleRows.NEBENTRAINER, calendarCollapsibleRows.ERSATZTRAINER],
    props.resource.id,
  );

  return (
    <Typography
      variant="label"
      color={isTrainerGroup ? elevateColorPalette.text.primaryOnAccent : calendarColors.accordionText}
    >
      {props.resource.name}
    </Typography>
  );
};

const Location = ({ resource }: { readonly resource: MbscResource }) => (
  <Box id="location" sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
    <LocationOnOutlined sx={{ marginRight: 1, fontSize: '20px' }} />
    <Typography variant="label">{resource.name}</Typography>
  </Box>
);

type CalendarResourceProps = {
  readonly resource: MbscResource;
  readonly year: number;
};

export const DisplayCalendarResource = (props: CalendarResourceProps) => {
  const { resource, year } = props;

  if (resource instanceof AbstractCalendarResource) {
    if (isExperteResource(resource)) {
      return (
        <HoverCard title={<ResourceTooltip resource={resource} year={year} />} placement="right" arrow>
          <ExperteResource resource={resource} />
        </HoverCard>
      );
    }

    if (isSeminarraumResource(resource) || isGruppenraumResource(resource) || isStandortResource(resource)) {
      return (
        <HoverCard title={<ResourceTooltip resource={resource} year={year} />} placement="right" arrow>
          <RaumOrStandortResource resource={resource} />
        </HoverCard>
      );
    }
  }

  if (resource instanceof LocationResource) {
    return <Location resource={resource} />;
  }

  return <ResourceGroup resource={resource} />;
};
